import React, { useEffect, useState } from 'react';
import '../css/AddArticlePage.css';
import { createReview } from '../api/reviewAPI';
import { cityAutoComplete, roleAutoComplete } from '../api/utilAPI';
import { useNavigate } from 'react-router-dom';
import SimpleMdeReact from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';

const initialArticleData = {
  title: '',
  companyName: '',
  role: '',
  location: '',
  text: '',
  interviewDate: '',
  experienceLevel: '',
  difficulty: '',
  outcome: '',
  compensation: '',
  applicationSource: '',
  preparationTime: '',
  rounds: '',
  questions: '',
  tips: '',
  anonymous: false,
};

const markdownOptions = {
  spellChecker: false,
  status: false,
  autofocus: false,
  placeholder: 'Write the full interview experience. Cover each round, what surprised you, and how the process felt.',
};

const AddArticlePage = () => {
  const [articleData, setArticleData] = useState(initialArticleData);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showRoleSuggestions, setShowRoleSuggestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (articleData.location.trim().length < 2) {
      setCitySuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const cities = await cityAutoComplete(articleData.location.trim());
        setCitySuggestions(cities);
      } catch (loadError) {
        console.error('Failed to fetch city suggestions:', loadError);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [articleData.location]);

  useEffect(() => {
    if (articleData.role.trim().length < 2) {
      setRoleSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const roles = await roleAutoComplete(articleData.role.trim());
        setRoleSuggestions(roles);
      } catch (loadError) {
        console.error('Failed to fetch role suggestions:', loadError);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [articleData.role]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setArticleData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await createReview(articleData);
      setArticleData(initialArticleData);
      navigate('/');
    } catch (submitError) {
      console.error('Failed to create article:', submitError);
      setError(submitError.message || 'Unable to publish interview right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="article-editor-shell">
      <section className="article-editor-card">
        <div className="editor-intro">
          <span className="editor-eyebrow">Share your interview</span>
          <h1>Turn your experience into a high-signal writeup.</h1>
          <p>
            Structure the essentials, then add the details candidates actually care about:
            rounds, difficulty, outcome, prep, and advice.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="form-article">
          {error && <div className="editor-alert">{error}</div>}

          <div className="article-grid article-grid-primary">
            <div className="form-group">
              <label htmlFor="title">Headline</label>
              <input
                type="text"
                id="title"
                name="title"
                value={articleData.title}
                onChange={handleChange}
                className="form-control"
                placeholder="Amazon SDE II interview experience from final loop"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="companyName">Company</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={articleData.companyName}
                onChange={handleChange}
                className="form-control"
                placeholder="Stripe"
                required
              />
            </div>
          </div>

          <div className="article-grid">
            <div className="form-group">
              <label htmlFor="role">Role</label>
              <input
                type="text"
                id="role"
                name="role"
                value={articleData.role}
                onChange={handleChange}
                onFocus={() => setShowRoleSuggestions(true)}
                onBlur={() => setTimeout(() => setShowRoleSuggestions(false), 150)}
                className="form-control"
                autoComplete="off"
                placeholder="Backend Engineer"
                required
              />
              {showRoleSuggestions && roleSuggestions.length > 0 && (
                <ul className="suggestion-list">
                  {roleSuggestions.map((role, index) => (
                    <li
                      key={`${role.job_title}-${index}`}
                      onMouseDown={() => {
                        setArticleData((current) => ({ ...current, role: role.job_title }));
                        setShowRoleSuggestions(false);
                      }}
                    >
                      {role.job_title}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={articleData.location}
                onChange={handleChange}
                onFocus={() => setShowCitySuggestions(true)}
                onBlur={() => setTimeout(() => setShowCitySuggestions(false), 150)}
                className="form-control"
                autoComplete="off"
                placeholder="Seattle, WA"
                required
              />
              {showCitySuggestions && citySuggestions.length > 0 && (
                <ul className="suggestion-list">
                  {citySuggestions.map((city) => {
                    const label = `${city.cityName}, ${city.stateCode}, ${city.countryCode}`;
                    return (
                      <li
                        key={label}
                        onMouseDown={() => {
                          setArticleData((current) => ({ ...current, location: label }));
                          setShowCitySuggestions(false);
                        }}
                      >
                        {label}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="interviewDate">Interview date</label>
              <input
                type="date"
                id="interviewDate"
                name="interviewDate"
                value={articleData.interviewDate}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="experienceLevel">Experience level</label>
              <select
                id="experienceLevel"
                name="experienceLevel"
                value={articleData.experienceLevel}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select</option>
                <option value="Intern">Intern</option>
                <option value="New Grad">New Grad</option>
                <option value="0-2 years">0-2 years</option>
                <option value="3-5 years">3-5 years</option>
                <option value="6+ years">6+ years</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="difficulty">Difficulty</label>
              <select
                id="difficulty"
                name="difficulty"
                value={articleData.difficulty}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
                <option value="Very Hard">Very Hard</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="outcome">Outcome</label>
              <select
                id="outcome"
                name="outcome"
                value={articleData.outcome}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select</option>
                <option value="Offer">Offer</option>
                <option value="Rejected">Rejected</option>
                <option value="Ghosted">Ghosted</option>
                <option value="Withdrew">Withdrew</option>
                <option value="In Progress">In Progress</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="compensation">Compensation</label>
              <input
                type="text"
                id="compensation"
                name="compensation"
                value={articleData.compensation}
                onChange={handleChange}
                className="form-control"
                placeholder="$180k TC"
              />
            </div>

            <div className="form-group">
              <label htmlFor="applicationSource">Applied via</label>
              <input
                type="text"
                id="applicationSource"
                name="applicationSource"
                value={articleData.applicationSource}
                onChange={handleChange}
                className="form-control"
                placeholder="Referral, LinkedIn, Careers page"
              />
            </div>

            <div className="form-group">
              <label htmlFor="preparationTime">Prep time</label>
              <input
                type="text"
                id="preparationTime"
                name="preparationTime"
                value={articleData.preparationTime}
                onChange={handleChange}
                className="form-control"
                placeholder="3 weeks, 2 hours per day"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="story">Full story</label>
            <SimpleMdeReact
              id="story"
              value={articleData.text}
              onChange={(value) => setArticleData((current) => ({ ...current, text: value }))}
              options={markdownOptions}
            />
          </div>

          <div className="article-grid article-grid-secondary">
            <div className="form-group">
              <label htmlFor="rounds">Interview rounds</label>
              <textarea
                id="rounds"
                name="rounds"
                value={articleData.rounds}
                onChange={handleChange}
                className="form-control textarea-control"
                placeholder="Phone screen, onsite loop, system design, hiring manager..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="questions">Questions asked</label>
              <textarea
                id="questions"
                name="questions"
                value={articleData.questions}
                onChange={handleChange}
                className="form-control textarea-control"
                placeholder="Top coding, behavioral, and system design questions..."
              />
            </div>
          </div>

          <div className="article-grid article-grid-secondary">
            <div className="form-group">
              <label htmlFor="tips">Tips for candidates</label>
              <textarea
                id="tips"
                name="tips"
                value={articleData.tips}
                onChange={handleChange}
                className="form-control textarea-control"
                placeholder="What to focus on, what surprised you, and what you would do differently."
              />
            </div>

            <div className="editor-side-card">
              <h3>Publishing checklist</h3>
              <ul>
                <li>Include both what went well and what felt hard.</li>
                <li>Add specific rounds and question themes.</li>
                <li>Use anonymous mode if that makes sharing safer.</li>
              </ul>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="anonymous"
                  checked={articleData.anonymous}
                  onChange={handleChange}
                />
                <span>Post anonymously</span>
              </label>
            </div>
          </div>

          <button className="btn btn-block" type="submit" disabled={submitting}>
            {submitting ? 'Publishing...' : 'Publish interview'}
          </button>
        </form>
      </section>
    </main>
  );
};

export default AddArticlePage;
